import { Component, ErrorInfo, ReactNode } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { error } = this.state;
    const isDev = process.env.NODE_ENV !== 'production';

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 720, width: '100%', textAlign: 'center' }}>
          <Typography variant="h3">เกิดข้อผิดพลาด</Typography>
          <Typography variant="body1" color="text.secondary">
            ขออภัย ระบบพบปัญหาที่ไม่คาดคิด กรุณาลองโหลดหน้านี้ใหม่อีกครั้ง
          </Typography>
          {isDev && error && (
            <Box
              component="pre"
              sx={{
                width: '100%',
                maxHeight: 320,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontSize: 12,
                textAlign: 'left',
                whiteSpace: 'pre-wrap'
              }}
            >
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </Box>
          )}
          <Button variant="contained" onClick={this.handleReload}>
            โหลดหน้าใหม่
          </Button>
        </Stack>
      </Box>
    );
  }
}
